/**
 * Validation result for user profile data
 */
export type UserValidationResult = {
  isValid: boolean;
  errors: {
    name?: boolean;
    birthday?: boolean;
  };
};

/**
 * Validates user name
 * @param name - User name to validate
 * @returns True if valid, false otherwise
 */
export function validateUserName(name: string | null | undefined): boolean {
  return !!(name && name.length > 1);
}

/**
 * Validates user birthday (always valid for now)
 * @param birthday - Birthday string or Date
 * @returns True (always valid)
 */
export function validateUserBirthday(
  birthday: string | Date | null | undefined,
): boolean {
  // Currently no validation rules, but keeping this for future use
  return true;
}

/**
 * Validates all user profile data
 * @param name - User name
 * @param birthday - User birthday
 * @returns Validation result with errors
 */
export function validateUserProfileData(
  name: string | null | undefined,
  birthday: string | Date | null | undefined,
): UserValidationResult {
  const errors = {
    name: !validateUserName(name),
    birthday: !validateUserBirthday(birthday),
  };

  const isValid = !Object.values(errors).some((error) => error === true);

  return {
    isValid,
    errors,
  };
}

/**
 * Validates file size
 * @param file - File to validate
 * @param maxSizeMB - Maximum file size in MB
 * @returns True if valid, false otherwise
 */
export function validateFileSize(file: File, maxSizeMB: number): boolean {
  const fileSizeMB = file.size / 1024 / 1024;
  return fileSizeMB < maxSizeMB;
}

/**
 * Converts a date string (YYYY-MM-DD) to a Date object
 * @param dateString - Date string in YYYY-MM-DD format
 * @returns Date object or null if empty
 */
export function parseDateString(dateString: string | null): Date | null {
  if (!dateString) {
    return null;
  }
  return new Date(dateString);
}

/**
 * Converts a Date object to YYYY-MM-DD format
 * @param date - Date object
 * @returns Date string in YYYY-MM-DD format or empty string
 */
export function formatDateToString(date: Date | null | undefined): string {
  if (!date) {
    return "";
  }
  if (date instanceof Date) {
    return date.toISOString().split("T")[0] ?? "";
  }
  return "";
}
